/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Real-Time QA Linter Diagnostics List Component (Task 10.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Binary,
  Tag,
  HelpCircle,
  Maximize2,
  Ban,
  ShieldAlert,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

interface QaWarningListProps {
  warnings: string[];
  targetText: string;
  sourceText: string;
  className?: string;
}

export const QaWarningList: React.FC<QaWarningListProps> = ({
  warnings,
  targetText,
  sourceText: _sourceText,
  className = '',
}) => {
  const { t } = useTranslation();

  const resolveWarningIcon = (msg: string) => {
    if (msg.toLowerCase().includes('number') || msg.toLowerCase().includes('digit')) {
      return <Binary className="size-4 text-rose-500" />;
    }
    if (msg.toLowerCase().includes('tag') || msg.toLowerCase().includes('placeholder')) {
      return <Tag className="size-4 text-amber-500" />;
    }
    if (msg.toLowerCase().includes('punctuation')) {
      return <HelpCircle className="size-4 text-amber-500" />;
    }
    if (msg.toLowerCase().includes('expansion') || msg.toLowerCase().includes('length')) {
      return <Maximize2 className="size-4 text-primary" />;
    }
    if (msg.toLowerCase().includes('forbidden')) {
      return <Ban className="size-4 text-rose-500" />;
    }
    return <AlertTriangle className="size-4 text-amber-500" />;
  };

  if (!targetText.trim()) {
    return (
      <div className={`p-4 rounded-lg bg-surface-elevated/40 border border-surface-border text-center text-xs text-foreground-muted space-y-1 ${className}`}>
        <p className="font-medium text-foreground">{t('cat.qaUntranslatedTitle')}</p>
        <p className="text-[11px] text-foreground-muted/80">{t('cat.qaUntranslatedDesc')}</p>
      </div>
    );
  }

  if (warnings.length === 0) {
    return (
      <div className={`p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center gap-3 ${className}`}>
        <div className="p-2 rounded-full bg-emerald-500/20 text-emerald-500 flex-shrink-0">
          <CheckCircle2 className="size-5" />
        </div>
        <div>
          <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            {t('cat.qaPassedTitle')}
          </h4>
          <p className="text-[11px] text-foreground-muted mt-0.5">
            {t('cat.qaPassedDesc')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2.5 ${className}`}>
      <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-600 dark:text-amber-400 font-medium">
        <ShieldAlert className="size-4 flex-shrink-0" />
        <span>
          {warnings.length} {t('cat.qaWarningsFound')}
        </span>
      </div>

      <div className="space-y-2">
        {warnings.map((warn, index) => (
          <div
            key={index}
            className="p-3 rounded-lg border border-amber-500/25 bg-surface hover:bg-surface-elevated/60 transition-colors flex items-start gap-2.5 text-xs"
          >
            <div className="p-1 rounded bg-surface-elevated border border-surface-border flex-shrink-0 mt-0.5">
              {resolveWarningIcon(warn)}
            </div>
            <div className="flex-1 space-y-1">
              <p className="font-semibold text-foreground">{warn}</p>
              <p className="text-[11px] text-foreground-muted">
                {t('cat.qaCheckNotice')}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
